/**
 * Refine AuthProvider — SSO через Drafter ID (B-1973).
 *
 * Вход/регистрация — только на `id.drafter.ru`. Своей формы у Акселератора нет:
 * личность приходит общей httpOnly-cookie `.drafter.ru`, которую бэкенд Акселератора
 * обменивает на локальный access (`exchange-drafter`). Доступ в панель — при
 * `is_admin` (роль admin/super-admin монолита) ИЛИ `is_mentor` (верифицированная
 * анкета `Mentor` монолита, B-2221).
 * Сам предикат — `lib/panel-access`, чтобы точки входа не разъезжались.
 *
 * ⚠️ `login()` в этом приложении НЕ вызывается: ни `useLogin`, ни `<AuthPage>` в
 * `src` нет, единственная реальная точка входа — `pages/login/index.tsx` (она
 * делает обмен сама). Метод обязателен по интерфейсу `AuthProvider` и оставлен
 * рабочим, но правку гейта надо вносить в обоих местах — на B-2175 поправили
 * только его, и роль ментора упиралась в «нет доступа к админ-панели».
 *
 * check(): нет локального access → на `/login` (там авто-обмен по cookie / редирект
 * в IdP). Есть access → сверяем `/v1/auth/me` (жив ли, и есть ли хоть одна роль).
 *
 * getPermissions() → "admin" | "mentor" — используется в App.tsx для role-gate.
 * getIdentity() → { ..., isAdmin, isMentor } — для условного рендера в UI.
 */

import type { AuthProvider } from "@refinedev/core";
import axios from "axios";

import { exchangeViaCookie, redirectToDrafterId } from "../lib/drafter-auth";
import { api } from "../lib/api";
import { hasPanelAccess, homePathFor, panelRoleOf } from "../lib/panel-access";
import { tokenStorage } from "../lib/token-storage";

interface MeResponse {
  id: number;
  name: string | null;
  surname: string | null;
  roles: string[];
  is_admin: boolean;
  is_mentor: boolean;
}

export type { AppRole } from "../lib/panel-access";

const NO_PANEL_ACCESS = "У этого аккаунта нет доступа к панели.";

export const authProvider: AuthProvider = {
  /** Программный обмен cookie → сессия (форму заменяет авто-handler на /login). */
  async login() {
    try {
      const { accessToken, user } = await exchangeViaCookie();
      if (!hasPanelAccess(user)) {
        return { success: false, error: { name: "Forbidden", message: NO_PANEL_ACCESS } };
      }
      tokenStorage.saveAccessToken(accessToken);
      // Mentors land on the homework queue; admins land on the dashboard root.
      return { success: true, redirectTo: homePathFor(panelRoleOf(user)) };
    } catch (error) {
      return {
        success: false,
        error: { name: "LoginError", message: "Не удалось войти через Drafter ID." },
        redirectTo: "/login",
        ...(axios.isAxiosError(error) && { cause: error }),
      };
    }
  },

  async logout() {
    // Локального сеанса больше нет; централизованный выход — на стороне IdP.
    tokenStorage.clear();
    redirectToDrafterId();
    return { success: true };
  },

  async check() {
    if (!tokenStorage.getAccessToken()) {
      return { authenticated: false, redirectTo: "/login" };
    }
    try {
      const { data } = await api.get<MeResponse>("/v1/auth/me");
      if (!hasPanelAccess(data)) {
        tokenStorage.clear();
        return { authenticated: false, redirectTo: "/login" };
      }
      return { authenticated: true };
    } catch {
      tokenStorage.clear();
      return { authenticated: false, redirectTo: "/login" };
    }
  },

  async getPermissions() {
    try {
      const { data } = await api.get<MeResponse>("/v1/auth/me");
      return panelRoleOf(data);
    } catch {
      return null;
    }
  },

  async getIdentity() {
    try {
      const { data } = await api.get<MeResponse>("/v1/auth/me");
      const fullName = [data.name, data.surname].filter(Boolean).join(" ").trim();
      return {
        id: data.id,
        name: fullName || data.roles.join(", ") || "Пользователь",
        roles: data.roles,
        isAdmin: data.is_admin,
        isMentor: data.is_mentor,
      };
    } catch {
      return null;
    }
  },

  async onError(error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      return { logout: true, redirectTo: "/login" };
    }
    return {};
  },
};
